import React, { useState } from 'react'
import { X, Upload, AlertCircle, CheckCircle, Send, Image, Activity } from 'lucide-react'

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-soft)' }}>
        {label}{hint && <span style={{ color: 'var(--text-faint)', fontWeight: 400 }}> · {hint}</span>}
      </label>
      {children}
    </div>
  )
}

const CATEGORIES = ['Technical Issue', 'Billing', 'Content Change', 'Feature Request', 'Other']
const PRIORITIES = ['Low', 'Normal', 'High']

const SupportForm = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({ subject: '', message: '', priority: 'Normal', category: 'Content Change', screenshot: null })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const handleFileSelect = (file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please select an image file (PNG, JPG, GIF, WEBP)'); return }
    if (file.size > 10 * 1024 * 1024) { setError('File size must be less than 10 MB'); return }
    setFormData(prev => ({ ...prev, screenshot: file }))
    setError('')
  }

  const handleSubmit = async (e) => {
    e?.preventDefault()
    if (!formData.subject.trim()) { setError('Subject is required'); return }
    if (!formData.message.trim()) { setError('Message is required'); return }
    setIsSubmitting(true)
    setError('')
    try {
      const submitData = new FormData()
      submitData.append('subject', formData.subject.trim())
      submitData.append('message', formData.message.trim())
      submitData.append('priority', formData.priority.toLowerCase())
      submitData.append('category', formData.category)
      if (formData.screenshot) submitData.append('screenshot', formData.screenshot)
      await onSubmit(submitData)
      setSuccess(true)
      setTimeout(() => { setFormData({ subject: '', message: '', priority: 'Normal', category: 'Content Change', screenshot: null }); setSuccess(false); onClose() }, 2000)
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '42rem', padding: 0 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', margin: 0 }}>Contact support</h3>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>Tell us what you need — we'll take it from here.</div>
          </div>
          <button className="site-action" onClick={onClose}><X style={{ width: 15, height: 15 }} /></button>
        </div>

        {/* Success state */}
        {success && (
          <div style={{ padding: '44px 22px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <span className="icon-tile success" style={{ width: 64, height: 64, fontSize: 30 }}>
              <CheckCircle style={{ width: 28, height: 28 }} />
            </span>
            <h2 style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>Request submitted!</h2>
            <div style={{ color: 'var(--text-muted)', fontSize: 13.5, lineHeight: 1.6, maxWidth: 380 }}>
              We've sent a confirmation to your email. Our team will be in touch shortly — typically within 4 hours.
            </div>
          </div>
        )}

        {/* Form */}
        {!success && (
          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && (
              <div style={{ padding: '10px 14px', background: 'var(--danger-soft)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertCircle style={{ width: 15, height: 15, color: 'var(--danger)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--danger)' }}>{error}</span>
              </div>
            )}

            <Field label="Subject">
              <input className="input" placeholder="Briefly, what do you need help with?" value={formData.subject} onChange={e => setFormData(p => ({ ...p, subject: e.target.value }))} maxLength={200} />
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Field label="Category">
                <select className="input" value={formData.category} onChange={e => setFormData(p => ({ ...p, category: e.target.value }))}>
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Priority">
                <div className="segmented" style={{ width: '100%' }}>
                  {PRIORITIES.map(p => (
                    <button key={p} type="button" className={formData.priority === p ? 'on' : ''} style={{ flex: 1, justifyContent: 'center', padding: '8px 6px' }} onClick={() => setFormData(prev => ({ ...prev, priority: p }))}>
                      {p}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <Field label="Description">
              <textarea
                className="input"
                rows={5}
                style={{ resize: 'vertical', lineHeight: 1.6 }}
                placeholder="Share as much detail as you can — page URLs, what you'd like changed, and any deadlines."
                value={formData.message}
                onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
              />
            </Field>

            <Field label="Attachments" hint="up to 3 files">
              <div
                style={{ height: 108, borderRadius: 12, border: `1.5px dashed ${dragOver ? 'var(--accent)' : 'var(--border-strong)'}`, background: dragOver ? 'var(--accent-soft)' : 'var(--surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 9, cursor: 'pointer', transition: 'all 0.15s' }}
                onClick={() => document.getElementById('sf-file-input').click()}
                onDrop={e => { e.preventDefault(); setDragOver(false); handleFileSelect(e.dataTransfer.files[0]) }}
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
              >
                <span className="icon-tile" style={{ width: 38, height: 38, fontSize: 17 }}><Upload style={{ width: 16, height: 16 }} /></span>
                <div style={{ fontSize: 12.5, color: 'var(--text-soft)', fontWeight: 500 }}>Drag files here, or click to browse</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>PNG, JPG, PDF · max 10 MB each</div>
                <input id="sf-file-input" type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0])} />
              </div>
              {formData.screenshot && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                  <div className="attach-chip">
                    <span className="a-icon"><Image style={{ width: 13, height: 13 }} /></span>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span style={{ color: 'var(--text)', fontWeight: 500, fontSize: 12 }}>{formData.screenshot.name}</span>
                      <span style={{ fontSize: 10.5, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{(formData.screenshot.size / 1024).toFixed(0)} KB</span>
                    </div>
                    <button className="site-action" style={{ width: 22, height: 22, marginLeft: 4 }} type="button" onClick={() => setFormData(p => ({ ...p, screenshot: null }))}>
                      <X style={{ width: 11, height: 11 }} />
                    </button>
                  </div>
                </div>
              )}
            </Field>
          </div>
        )}

        {/* Footer */}
        {!success && (
          <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-muted)' }}>
              <span className="icon-tile" style={{ width: 26, height: 26, fontSize: 12 }}><Activity style={{ width: 12, height: 12 }} /></span>
              Average response time: <strong style={{ color: 'var(--text-soft)' }}>under 4 hours</strong>
            </span>
            <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : <><Send style={{ width: 13, height: 13 }} /> Submit request</>}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default SupportForm
